import React, { useState, useEffect } from 'react';
import { GURYAHA_ITEMS } from '../../utils/constants';
import { isLegacyMukayfRow, normalizeGuryahaItemName } from '../../utils/helpers';

const buildGuryahaCatalog = () =>
  GURYAHA_ITEMS.map((item) => ({
    itemName: item.name,
    options: item.options,
    selection: '',
    damaged: false,
    quantity: 0,
    notes: ''
  }));

const GuristaGuryahaTable = ({ items = [], onChange, fullCatalog = false }) => {
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (fullCatalog) {
      const savedByName = new Map();
      items.forEach((i) => {
        if (isLegacyMukayfRow(i)) return;
        savedByName.set(i.itemName, i);
      });
      setTableData(
        buildGuryahaCatalog().map((row) => ({
          ...row,
          ...(savedByName.get(row.itemName) || {})
        }))
      );
      return;
    }
    if (items.length > 0) {
      setTableData(items.filter((i) => !isLegacyMukayfRow(i)));
    } else {
      setTableData(buildGuryahaCatalog());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectionChange = (index, value) => {
    const newData = [...tableData];
    newData[index].selection = value;
    setTableData(newData);
    sendChanges(newData);
  };

  const handleDamageChange = (index) => {
    const newData = [...tableData];
    newData[index].damaged = !newData[index].damaged;
    setTableData(newData);
    sendChanges(newData);
  };

  const handleQuantityChange = (index, value) => {
    const newData = [...tableData];
    newData[index].quantity = parseInt(value, 10) || 0;
    setTableData(newData);
    sendChanges(newData);
  };

  const handleNotesChange = (index, value) => {
    const newData = [...tableData];
    newData[index].notes = value;
    setTableData(newData);
    sendChanges(newData);
  };

  const sendChanges = (data) => {
    const allItems = data
      .filter((item) => !isLegacyMukayfRow(item))
      .map(({ mukayfIc: _drop, ...item }) => ({
        itemName: item.itemName,
        selection: item.selection || '',
        damaged: Boolean(item.damaged),
        quantity: parseInt(item.quantity, 10) || 0,
        notes: item.notes || ''
      }));
    onChange(allItems);
  };

  return (
    <div className="space-y-2">
      <div className="hidden md:grid md:grid-cols-12 gap-2 bg-red-700 text-white p-3 rounded-t-lg text-sm font-medium">
        <div className="col-span-3">Alaabta</div>
        <div className="col-span-3">Nooca</div>
        <div className="col-span-2 text-center">Dhaawac</div>
        <div className="col-span-2 text-center">Tirada</div>
        <div className="col-span-2">Faallo</div>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {tableData.map((row, index) => (
          <div
            key={row.itemName}
            className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
          >
            <div className="md:col-span-3 font-semibold text-[#C62828] flex items-center">
              <span className="md:hidden text-gray-500 mr-2">Alaabta:</span>
              {normalizeGuryahaItemName(row.itemName)}
            </div>

            <div className="md:col-span-3">
              <label className="md:hidden text-xs text-gray-500 mb-1 block">Nooca:</label>
              <select
                value={row.selection}
                onChange={(e) => handleSelectionChange(index, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                {(row.options ||
                  GURYAHA_ITEMS.find((i) => i.name === row.itemName)?.options ||
                  []
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 flex items-center justify-start md:justify-center">
              <label className="md:hidden text-xs text-gray-500 mr-2">Dhaawac:</label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={row.damaged}
                  onChange={() => handleDamageChange(index)}
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-600 md:hidden">
                  {row.damaged ? 'Haa' : 'Maya'}
                </span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="md:hidden text-xs text-gray-500 mb-1 block">Tirada:</label>
              <input
                type="number"
                min="0"
                value={row.quantity || ''}
                onChange={(e) => handleQuantityChange(index, e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="md:hidden text-xs text-gray-500 mb-1 block">Faallo:</label>
              <input
                type="text"
                value={row.notes || ''}
                onChange={(e) => handleNotesChange(index, e.target.value)}
                placeholder="..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 p-3 rounded-lg mt-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Alaabo la doortay:</span>{' '}
          {tableData.filter((item) => item.selection || item.quantity > 0).length} / {tableData.length}
        </p>
      </div>
    </div>
  );
};

export default GuristaGuryahaTable;
